import React from "react";
import { store } from "react-notifications-component";
import { KuikaAppManager } from "../shared/utilty/kuika-app-manager";

// Service Worker update state
let waitingServiceWorker: ServiceWorker | null = null;
let onUpdateAvailableCallback: (() => void) | null = null;

/**
 * Register a callback to be notified when a new app version is available
 */
function onUpdateAvailable(callback: () => void) {
  onUpdateAvailableCallback = callback;
  // If update is already waiting, call immediately
  if (waitingServiceWorker) {
    callback();
  }
}

/**
 * Apply the pending update - activates the new service worker and reloads the page
 */
function applyUpdate() {
  if (waitingServiceWorker) {
    navigator.serviceWorker.addEventListener(
      "controllerchange",
      () => {
        window.location.reload();
      },
      { once: true }
    );
    waitingServiceWorker.postMessage("SKIP_WAITING");
  }
}

function showUpdateNotification() {
  const isTurkish = localStorage.getItem("ml") === "tr_TR";

  const message = React.createElement(
    "div",
    {
      style: {
        display: "flex",
        flexDirection: "column" as const,
        gap: "8px",
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji'"
      }
    },
    React.createElement("span", null, isTurkish ? "Yeni uygulama sürümü mevcut." : "New app version available."),
    React.createElement(
      "button",
      {
        onClick: () => applyUpdate(),
        style: {
          padding: "4px 12px",
          border: "none",
          borderRadius: "4px",
          backgroundColor: "#fff",
          color: "#333",
          cursor: "pointer",
          fontWeight: "bold",
          alignSelf: "flex-start"
        }
      },
      isTurkish ? "Yenile" : "Refresh"
    )
  );

  store.addNotification({
    title: isTurkish ? "Güncelleme!" : "Update!",
    message,
    type: "info",
    container: "bottom-right",
    animationIn: ["animated", "flipInX"],
    animationOut: ["animated", "fadeOut"],
    dismiss: {
      duration: 0,
      pauseOnHover: true,
      showIcon: true
    }
  });
}

function waitForPageSettled(): Promise<void> {
  return new Promise((resolve) => {
    function afterLoad() {
      setTimeout(resolve, 5000);
    }
    if (document.readyState === "complete") {
      afterLoad();
    } else {
      window.addEventListener("load", afterLoad, { once: true });
    }
  });
}

/**
 * Initialize app update monitoring via service worker
 */
export async function initAppUpdateMonitoring() {
  // Service Workers only work with http:, https:, or localhost
  // Skip for custom protocols (e.g., kuika:// in Electron)
  const protocol = window.location.protocol;
  if (protocol !== "http:" && protocol !== "https:") {
    return;
  }

  if (!("serviceWorker" in navigator)) {
    return;
  }

  // In dev server mode no sw.js is emitted — skip update monitoring entirely.
  if (process.env.DEV_SERVER === "true") {
    return;
  }

  try {
    const swUrl = `${KuikaAppManager.GetFrontEndUrl()}/sw.js`;

    // Use register() instead of getRegistration() — it's idempotent for an
    // already-registered SW but also triggers an update check, ensuring the
    // browser always fetches the latest sw.js on page load.
    let registration = await navigator.serviceWorker.register(swUrl);

    // Recovery: if the active worker is in "redundant" state (failed activation)
    // or there's no controller despite having a registration, unregister and
    // re-register to clear the stuck state.
    if (!navigator.serviceWorker.controller && !registration.installing && !registration.waiting) {
      await registration.unregister();
      registration = await navigator.serviceWorker.register(swUrl);
    }

    let settled = false;

    // Handle any SW that's already waiting/installing at registration time (page-load update).
    if (registration.waiting) {
      registration.waiting.postMessage("SKIP_WAITING");
    }
    if (registration.installing) {
      registration.installing.addEventListener("statechange", function () {
        if (this.state === "installed") {
          this.postMessage("SKIP_WAITING");
        }
      });
    }

    // Listen for new updates.
    registration.addEventListener("updatefound", () => {
      const newWorker = registration.installing;
      if (newWorker) {
        newWorker.addEventListener("statechange", () => {
          if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
            if (settled) {
              // Genuine background update — show notification.
              waitingServiceWorker = newWorker;
              showUpdateNotification();
              if (onUpdateAvailableCallback) {
                onUpdateAvailableCallback();
              }
            } else {
              // Page-load update — silently activate.
              newWorker.postMessage("SKIP_WAITING");
            }
          }
        });
      }
    });

    // After the page has fully loaded, any future update is a background update.
    await waitForPageSettled();
    settled = true;

    // Periodically check for updates (every 60 seconds)
    setInterval(() => {
      registration.update();
    }, 60 * 1000);

    // Check for updates when the tab becomes visible again
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") {
        registration.update();
      }
    });
  } catch (error) {
    console.error("App update monitoring initialization failed", error);
  }
}
